import React from 'react';
import { AlertCircle, CreditCard, X } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';

const SubscriptionBanner = ({ subscriptionData, onDismiss }) => {
  const navigate = useNavigate();
  
  if (!subscriptionData?.subscription_message || subscriptionData?.subscription_status === 'active') return null;
  
  const { subscription_message, show_renew, subscription_status } = subscriptionData;
  const statusStyles = {
    active: {
      container: 'border-blue-200 bg-blue-50',
      icon: 'text-blue-600',
      text: 'text-blue-800',
      accent: 'text-blue-900 bg-blue-100',
      button: 'text-blue-600 border-blue-300 hover:bg-blue-100'
    },
    expiring_soon: {
      container: 'border-orange-200 bg-orange-50',
      icon: 'text-orange-600',
      text: 'text-orange-800',
      accent: 'text-orange-900 bg-orange-100',
      button: 'text-orange-600 border-orange-300 hover:bg-orange-100'
    },
    grace_period: {
      container: 'border-amber-200 bg-amber-50',
      icon: 'text-amber-600',
      text: 'text-amber-800',
      accent: 'text-amber-900 bg-amber-100',
      button: 'text-amber-700 border-amber-300 hover:bg-amber-100'
    },
    suspended: {
      container: 'border-red-200 bg-red-50',
      icon: 'text-red-600',
      text: 'text-red-800',
      accent: 'text-red-900 bg-red-100',
      button: 'text-red-600 border-red-300 hover:bg-red-100'
    },
    deleted: {
      container: 'border-slate-200 bg-slate-50',
      icon: 'text-slate-600',
      text: 'text-slate-800',
      accent: 'text-slate-900 bg-slate-100',
      button: 'text-slate-600 border-slate-300 hover:bg-slate-100'
    },
    expired: {
      container: 'border-red-200 bg-red-50',
      icon: 'text-red-600',
      text: 'text-red-800',
      accent: 'text-red-900 bg-red-100',
      button: 'text-red-600 border-red-300 hover:bg-red-100'
    }
  };

  const resolvedStatus = statusStyles[subscription_status] || statusStyles.expiring_soon;
  const isFinalState = ['expired', 'suspended', 'deleted'].includes(subscription_status);
  
  const handleRenew = () => {
    navigate('/subscription-lifecycle/renew');
  };
  
  return (
    <Alert className={`mb-4 ${resolvedStatus.container}`}>
      <AlertCircle className={`h-4 w-4 ${resolvedStatus.icon}`} />
      <AlertDescription className="flex items-center justify-between">
        <span className={resolvedStatus.text}>
          {subscription_message.split('**').map((part, index) => {
            // Even indexes (0, 2, 4...) are normal text, odd indexes (1, 3, 5...) are bold
            if (index % 2 === 1) {
              // This is bold content (the date)
              const dateClasses = `font-bold ${resolvedStatus.accent} px-2 py-1 rounded`;
              return <strong key={index} className={dateClasses}>{part}</strong>;
            } else {
              return <span key={index}>{part}</span>;
            }
          })}
                  </span>
        <div className="flex items-center gap-2">
          {show_renew && (
            <Button 
              size="sm" 
              variant="outline"
              onClick={handleRenew}
              className={resolvedStatus.button}
            >
              <CreditCard className="w-4 h-4 mr-1" />
              Renew Now
            </Button>
          )}
          {/* Don't allow dismiss for final states */}
          {!isFinalState && (
            <Button 
              size="sm" 
              variant="ghost"
              onClick={onDismiss}
              className={resolvedStatus.button}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default SubscriptionBanner;
